import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../services/global-environment';
import {
  NumberingConfig,
  NumberingResult,
  SequenceApiResponse
} from '../models/numbering-config.interface';
import {
  NumberingElement,
  SequenceElementConfig,
  DropdownElementConfig,
  YearElementConfig,
  MonthElementConfig,
  TextElementConfig
} from '../models/numbering-element.interface';

@Injectable({
  providedIn: 'root'
})
export class AutoNumberingService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * 根據配置生成自動編號
   * @param config 編號配置
   * @param dropdownValues 下拉選單選中的值
   * @returns 編號生成結果
   */
  async generateNumber(config: NumberingConfig, dropdownValues: { [key: string]: string } = {}): Promise<NumberingResult> {
    try {
      // 驗證配置
      const validation = this.validateConfig(config);
      if (!validation.isValid) {
        return {
          success: false,
          number: '',
          error: validation.error || '配置驗證失敗',
          elementValues: {}
        };
      }

      const elementValues: { [key: string]: string } = {};
      const numberParts: string[] = [];

      // 按順序處理每個元素
      const sortedElements = config.elements.sort((a, b) => a.order - b.order);

      for (const element of sortedElements) {
        const value = await this.generateElementValue(element, dropdownValues, config);
        elementValues[`${element.type}_${element.order}`] = value;
        numberParts.push(value);
      }

      // 組合最終編號
      const separator = config.separator || '';
      const finalNumber = numberParts.join(separator);

      return {
        success: true,
        number: finalNumber,
        elementValues
      };
    } catch (error) {
      return {
        success: false,
        number: '',
        error: error instanceof Error ? error.message : '編號生成失敗',
        elementValues: {}
      };
    }
  }

  /**
   * 生成單個元素的值
   * @param element 編號元素
   * @param dropdownValues 下拉選單值
   * @param config 完整的編號配置
   * @returns 元素值
   */
  private async generateElementValue(element: NumberingElement, dropdownValues: { [key: string]: string }, config: NumberingConfig): Promise<string> {
    switch (element.type) {
      case 'dropdown':
        return this.generateDropdownValue(element.config as DropdownElementConfig, dropdownValues, element.order);

      case 'sequence':
        return await this.generateSequenceValue(element.config as SequenceElementConfig, dropdownValues, config);

      case 'year':
        return this.generateYearValue(element.config as YearElementConfig);

      case 'month':
        return this.generateMonthValue(element.config as MonthElementConfig);

      case 'text':
        return this.generateTextValue(element.config as TextElementConfig);

      default:
        throw new Error(`不支援的元素類型: ${element.type}`);
    }
  }

  /**
   * 生成下拉選單值
   */
  private generateDropdownValue(config: DropdownElementConfig, dropdownValues: { [key: string]: string }, order: number): string {
    const key = `dropdown_${order}`;
    const selectedValue = dropdownValues[key];

    if (!selectedValue) {
      throw new Error(`下拉選單尚未選擇`);
    }

    // 找到對應的選項並回傳 label 而不是 value
    const selectedOption = config.options?.find(option => option.value === selectedValue);
    if (selectedOption) {
      return selectedOption.label;
    }

    // 如果找不到對應的選項，回傳原始值
    return selectedValue;
  }

  /**
   * 生成流水編號值
   */
  private async generateSequenceValue(config: SequenceElementConfig, dropdownValues: { [key: string]: string } = {}, numberingConfig: NumberingConfig): Promise<string> {
    let nextNumber = config.startFrom;

    // 如果有 API Key，從 API 取得最新編號
    if (config.apiKey) {
      try {
        nextNumber = await this.getNextSequenceFromApi(config.apiKey, dropdownValues, numberingConfig);
      } catch (error) {
        console.warn('API 取得流水號失敗，使用預設起始值:', error);
        nextNumber = config.startFrom;
      }
    }

    // 格式化為指定位數
    return nextNumber.toString().padStart(config.digits, '0');
  }

  /**
   * 從 API 取得下一個流水編號
   */
  private getNextSequenceFromApi(apiKey: string, dropdownValues: { [key: string]: string } = {}, config: NumberingConfig): Promise<number> {
    const companyid = environment.companyid;

    if (!companyid) {
      return Promise.reject(new Error('companyid 未設定'));
    }

    // 生成完整的編號規則（除了流水號）作為 keyword
    let keyword = '';
    try {
      const keywordParts: string[] = [];
      const sortedElements = config.elements.sort((a, b) => a.order - b.order);
      
      for (const element of sortedElements) {
        // 跳過流水編號元素
        if (element.type === 'sequence') {
          continue;
        }
        
        let elementValue = '';
        switch (element.type) {
          case 'dropdown':
            const dropdownConfig = element.config as DropdownElementConfig;
            const selectedValue = dropdownValues[`dropdown_${element.order}`];
            const selectedOption = dropdownConfig.options?.find(option => option.value === selectedValue);
            elementValue = selectedOption?.label || '';
            break;
          case 'year':
            const yearConfig = element.config as YearElementConfig;
            const currentYear = new Date().getFullYear();
            elementValue = yearConfig.format === 'YYYY' ? currentYear.toString() : currentYear.toString().slice(-2);
            break;
          case 'month':
            const monthConfig = element.config as MonthElementConfig;
            const currentMonth = new Date().getMonth() + 1;
            elementValue = monthConfig.format === 'MM' ? currentMonth.toString().padStart(2, '0') : currentMonth.toString();
            break;
          case 'text':
            const textConfig = element.config as TextElementConfig;
            elementValue = textConfig.text || '';
            break;
        }
        
        if (elementValue) {
          keywordParts.push(elementValue);
        }
      }
      
      // 用分隔符號組合成 keyword
      keyword = keywordParts.join(config.separator || '');
    } catch (error) {
      console.warn('生成 keyword 時發生錯誤:', error);
      // 如果生成失敗，降級到原本的邏輯（只用下拉選單）
      const dropdownElement = config.elements.find(element => element.type === 'dropdown');
      if (dropdownElement) {
        const dropdownConfig = dropdownElement.config as DropdownElementConfig;
        const selectedValue = dropdownValues[`dropdown_${dropdownElement.order}`];
        const selectedOption = dropdownConfig.options?.find(option => option.value === selectedValue);
        keyword = selectedOption?.label || '';
      }
    }

    const url = `${this.apiUrl}/api/dynamic-data/proxy`;
    const requestBody = {
      companyid: companyid,
      key: apiKey,
      keyword: keyword
    };

    return this.http.post<SequenceApiResponse>(url, requestBody)
      .pipe(
        map(response => {
          console.log('API 回應:', response);

          if (response.success && response.data?.nextNumber) {
            const nextNumber = response.data.nextNumber;
            return nextNumber;
          } else {
            throw new Error('API 回應格式錯誤');
          }
        }),
        catchError(error => {
          console.error('API 呼叫失敗:', error);
          return Promise.reject(error);
        })
      )
      .toPromise() as Promise<number>;
  }

  /**
   * 生成年份值
   */
  private generateYearValue(config: YearElementConfig): string {
    const currentYear = new Date().getFullYear();
    return config.format === 'YYYY' ? currentYear.toString() : currentYear.toString().slice(-2);
  }

  /**
   * 生成月份值
   */
  private generateMonthValue(config: MonthElementConfig): string {
    const currentMonth = new Date().getMonth() + 1;
    return config.format === 'MM' ? currentMonth.toString().padStart(2, '0') : currentMonth.toString();
  }

  /**
   * 生成固定文字值
   */
  private generateTextValue(config: TextElementConfig): string {
    return config.text || '';
  }

  /**
   * 生成預覽編號
   * @param config 編號配置
   * @returns 預覽編號字串
   */
  generatePreview(config: NumberingConfig): string {
    try {
      const numberParts: string[] = [];
      const sortedElements = config.elements.sort((a, b) => a.order - b.order);

      for (const element of sortedElements) {
        let preview = '';

        switch (element.type) {
          case 'dropdown':
            const dropdownConfig = element.config as DropdownElementConfig;
            preview = dropdownConfig.options.length > 0 ? dropdownConfig.options[0].label : '[選項]';
            break;
          case 'sequence':
            const seqConfig = element.config as SequenceElementConfig;
            preview = seqConfig.startFrom.toString().padStart(seqConfig.digits, '0');
            break;
          case 'year':
            const yearConfig = element.config as YearElementConfig;
            const year = new Date().getFullYear();
            preview = yearConfig.format === 'YYYY' ? year.toString() : year.toString().slice(-2);
            break;
          case 'month':
            const monthConfig = element.config as MonthElementConfig;
            const month = new Date().getMonth() + 1;
            preview = monthConfig.format === 'MM' ? month.toString().padStart(2, '0') : month.toString();
            break;
          case 'text':
            const textConfig = element.config as TextElementConfig;
            preview = textConfig.text || '[文字]';
            break;
        }

        numberParts.push(preview);
      }

      return numberParts.join(config.separator || '');
    } catch (error) {
      return '預覽失敗';
    }
  }

  /**
   * 驗證編號配置
   * @param config 編號配置
   * @returns 驗證結果
   */
  validateConfig(config: NumberingConfig): { isValid: boolean; error?: string } {
    const dropdownCount = config.elements.filter(element => element.type === 'dropdown').length;
    const sequenceCount = config.elements.filter(element => element.type === 'sequence').length;

    if (dropdownCount > 1) {
      return {
        isValid: false,
        error: '設定中只能包含一個下拉選單元素'
      };
    }

    if (sequenceCount > 1) {
      return {
        isValid: false,
        error: '設定中只能包含一個流水序號元素'
      };
    }

    // 檢查流水序號元素的 API Key 是否為必填
    const sequenceElement = config.elements.find(element => element.type === 'sequence');
    if (sequenceElement) {
      const sequenceConfig = sequenceElement.config as SequenceElementConfig;

      // API Key 現在是必填欄位
      if (!sequenceConfig.apiKey || sequenceConfig.apiKey.trim() === '') {
        return {
          isValid: false,
          error: 'API Key 為必填欄位，請設定用於取得最新編號的 API Key'
        };
      }

      // 檢查是否有其他元素可以作為區分依據（不再強制要求下拉選單）
      const nonSequenceElements = config.elements.filter(element => element.type !== 'sequence');
      if (nonSequenceElements.length === 0) {
        return {
          isValid: false,
          error: '使用 API 流水序號時，必須配置其他元素（如下拉選單、固定文字、年份、月份等）來區分不同的序號系列'
        };
      }
    }

    return { isValid: true };
  }
}
